import {
  useRef,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import type { FC, ReactNode } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import useWhiteLabelConfig from "src/contexts/config-context";
import { RouterLink } from "src/components/router-link";
import { paths } from "src/paths";
import toast from "react-hot-toast";

// Create context for express layout features
interface ExpressContextType {
  triggerConfetti?: (source?: "checklist" | "download") => void;
  viewMode?: "intake" | "download";
  setViewMode?: (mode: "intake" | "download") => void;
}

const ExpressContext = createContext<ExpressContextType>({});

export const useExpressContext = () => useContext(ExpressContext);

const LayoutRoot = styled("div")(({ theme }) => ({
  backgroundColor: "#fcfcfc",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "top center",
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  height: "100%",
  position: "relative",
}));

interface LayoutProps {
  children?: ReactNode;
}

export const AccessLayout: FC<LayoutProps> = (props) => {
  const { children } = props;
  const { professional_group } = useWhiteLabelConfig();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [viewMode, setViewMode] = useState<"intake" | "download">("intake");
  const imageRef = useRef<HTMLImageElement>(null);


  // Function to trigger confetti
  const triggerConfetti = useCallback((source?: "checklist" | "download") => {
    const message =
      source === "checklist"
        ? "Congratulations, you've created all documents! 🎉"
        : "Congratulations, you've signed all documents! 🎉";
    toast.success(message, {
      position: "top-center",
      duration: 5000,
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (
        imageRef.current &&
        professional_group?.app_logo &&
        professional_group?.total_app_logo_multiplier
      ) {
        const { naturalWidth, naturalHeight } = imageRef.current;
        const aspectRatio = naturalWidth / naturalHeight;

        // Check if mobile screen
        const isMobile = window.innerWidth < 600; // MUI 'sm' breakpoint
        const mobileMultiplier = 0.7; // 70% of desktop size on mobile

        let maxWidth = 180;
        let maxHeight = 30;

        // Adjust maxHeight for aspect ratios
        if (aspectRatio < 2.9) {
          maxHeight = 48;
        }
        if (aspectRatio >= 2.9 && aspectRatio <= 4) {
          maxHeight = 40;
        }
        if (aspectRatio > 4) {
          maxHeight = 35;
        }

        // Apply mobile reduction
        if (isMobile) {
          maxWidth = maxWidth * mobileMultiplier;
          maxHeight = maxHeight * mobileMultiplier;
        }

        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          if (maxWidth / aspectRatio < maxHeight) {
            setDimensions({
              width: maxWidth * professional_group?.total_app_logo_multiplier,
              height:
                (maxWidth / aspectRatio) *
                professional_group?.total_app_logo_multiplier,
            });
          } else {
            setDimensions({
              width:
                maxHeight *
                aspectRatio *
                professional_group?.total_app_logo_multiplier,
              height: maxHeight * professional_group?.total_app_logo_multiplier,
            });
          }
        } else {
          setDimensions({
            width: naturalWidth * professional_group?.total_app_logo_multiplier,
            height:
              naturalHeight * professional_group?.total_app_logo_multiplier,
          });
        }
      }
    };

    const img = imageRef.current;

    if (img && professional_group?.app_logo) {
      img.onload = handleResize;
      img.src = professional_group?.app_logo;
    }

    // Add resize listener to handle screen size changes
    window.addEventListener("resize", handleResize);

    return () => {
      if (img) {
        img.onload = null;
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [
    professional_group?.app_logo,
    professional_group?.total_app_logo_multiplier,
  ]);

  // Commented out auto-trigger - now triggered manually when completion reaches 100%
  // useEffect(() => {
  //   // Trigger confetti and toast after component mounts and children render
  //   const timer = setTimeout(() => {
  //     setShowConfetti(true);
  //     toast.success("Great job!", {
  //       position: "top-center",
  //       duration: 3000,
  //     });
  //   }, 100);
  //   return () => clearTimeout(timer);
  // }, []);

  // const parseFirstName = (name: string | undefined): string => {
  //   if (name) {
  //     const nameArray = name.split(" ");
  //     return nameArray[0];
  //   } else {
  //     return "";
  //   }
  // };

  return (
    <ExpressContext.Provider value={{ triggerConfetti, viewMode, setViewMode }}>
      <LayoutRoot>
        <Box component="header" sx={{ mt: 2, position: "relative", zIndex: 1 }}>
          <Container maxWidth="lg">
            <Stack
              direction="row"
              spacing={2}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Stack
                alignItems="center"
                component={RouterLink}
                direction="row"
                display="inline-flex"
                href={paths.dashboard.express}
                spacing={1}
                sx={{ textDecoration: "none" }}
              >
                <Box sx={{ display: "inline-flex" }}>
                  <img
                    ref={imageRef}
                    src={professional_group?.app_logo || undefined}
                    style={{
                      width: dimensions.width,
                      height: dimensions.height,
                    }}
                  />
                </Box>
              </Stack>
            </Stack>
          </Container>
        </Box>
        <Box
          sx={{
            alignItems: "top",
            display: "flex",
            justifyContent: "center",
            flex: "1 1 auto",
            mt: 0,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ px: { xs: 0, sm: 2, md: 4 } }}>
              <Box sx={{ my: 0 }}>{children}</Box>
            </Box>
          </Container>
        </Box>
      </LayoutRoot>
    </ExpressContext.Provider>
  );
};

AccessLayout.propTypes = {
  children: PropTypes.node as PropTypes.Validator<ReactNode>,
};
